import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import OriginMasterService from "../pages/dashboard/Locality/Service/originMasterService";
import {
  getBrandDomain,
  getBrandInfo,
  getSupportEmail,
  getWwwDomain,
  getSiteUrl,
} from "../utils/siteBrand";

/** Known Origin Master feature-flag names (API spelling preserved). */
export const FEATURE_FLAGS = {
  DISABLE_INDIAN_CURRICULUM: "disable_indian_circulum",
  /** When enabled, Student Form shows "Student Id" instead of "Admission Number". */
  AN_TO_STUDENT_ID: "AN_to_studentId",
  /** When enabled, Student Form shows "Enrollment Date" instead of "Date Of Admission". */
  DOA_TO_ENROLLMENT_DATE: "DOA_to_Enrollment_Date",
  /** When enabled, hide Aadhaar Number on Parent Student Profile. */
  DISABLE_AADHAAR_NUMBER: "disable_Aadhaar_Number",
  /** When enabled, hide Masked Aadhaar Card on Parent Student Profile. */
  DISABLE_MASKED_AADHAAR_CARD: "disable_Masked_Aadhaar_Card",
  /** When enabled, Student Details uses the US sample Excel for registration upload. */
  US_VERSION_STUDENT_REGISTRATION_UPLOAD: "US_version_student_registration_upload",
  /** When enabled, School Registration shows "Name on Invoice" instead of "Society Name". */
  SOCIETY_NAME_TO_NAME_ON_INVOICE: "Society_Name_to_Name_on_Invoice",
  /** When enabled, School Registration shows "Zip Code" instead of "Pin Code". */
  PIN_CODE_TO_ZIP_CODE: "Pin_Code_to_Zip_Code",
  /** When enabled, School Registration shows a US State dropdown. */
  STATE: "State",
  /** When enabled, School Registration shows DPA Signed (Y/N). */
  DPA_SIGNED: "DPA_Signed",
};

export function featureKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    // Ignore spaces / underscores / punctuation so Origin Master name variants still match
    // e.g. Pin_Code_to_Zip_Code === Pincode_to_Zip_Code === "Pin Code to Zip Code"
    .replace(/[^a-z0-9]+/g, "");
}

/** True when an enabled feature name matches any candidate (normalized). */
export function isEnabledFeatureMatch(features, ...candidates) {
  const wanted = new Set(
    candidates.map((c) => featureKey(c)).filter(Boolean),
  );
  if (!wanted.size) return false;
  for (const feature of Array.isArray(features) ? features : []) {
    if (!feature || feature.enabled === false) continue;
    const key = featureKey(feature.name);
    if (!key) continue;
    if (wanted.has(key)) return true;
    // Soft match for pin/zip label flags with wording differences.
    if (
      wanted.has("pincodetozipcode") &&
      key.includes("pin") &&
      key.includes("zip")
    ) {
      return true;
    }
  }
  return false;
}

/** Origin Master flag variants that mean: hide Select Curriculum Model; International-only. */
export function isDisableIndianCurriculumFlagName(name) {
  const key = featureKey(name);
  if (!key) return false;
  if (key === featureKey(FEATURE_FLAGS.DISABLE_INDIAN_CURRICULUM)) return true;
  // Exact / known aliases (incl. Origin Master typo: remove_select_circrulum)
  const aliases = [
    "disableindiancurriculum",
    "disableindiancirrculum",
    "disableindiancirculum",
    "removeindiancurriculum",
    "removeindiancirculum",
    "removeselectcircrulum",
    "removeselectcirculum",
    "removeselectcurriculum",
    "hideselectcurriculum",
    "hideselectcirculum",
    "disableselectcurriculum",
    "disablecurriculummodel",
    "removecurriculummodel",
  ];
  if (aliases.includes(key)) return true;
  const hideOrRemove =
    key.includes("disable") || key.includes("remove") || key.includes("hide");
  const hasCurriculum =
    key.includes("circul") ||
    key.includes("curricul") ||
    key.includes("cirrcul") ||
    key.includes("circrul");
  // remove_select_circrulum / hide_select_curriculum (no "indian" required)
  if (hideOrRemove && hasCurriculum && key.includes("select")) return true;
  // disable_indian_circulum style
  if (hideOrRemove && key.includes("indian") && hasCurriculum) return true;
  return false;
}

export function hasDisableIndianCurriculumFlag(features = []) {
  return (Array.isArray(features) ? features : []).some(
    (f) => f && f.enabled !== false && isDisableIndianCurriculumFlagName(f.name),
  );
}

/**
 * Normalize features from either top-level `features` or any `configs[].features`.
 * Prefers top-level `features` when present; otherwise merges config feature lists.
 */
export function resolveFeaturesFromResponse(data = {}) {
  const map = new Map();
  const addList = (list) => {
    for (const f of Array.isArray(list) ? list : []) {
      const name = String(f?.name || "").trim();
      if (!name) continue;
      const key = featureKey(name);
      if (map.has(key)) continue;
      map.set(key, {
        name,
        description: String(f?.description || "").trim(),
        enabled: f?.enabled !== false,
      });
    }
  };

  // Prefer top-level features first, then merge any configs[].features
  // so flags are not dropped when both shapes are present.
  addList(data.features);
  const configs = Array.isArray(data.configs) ? data.configs : [];
  for (const cfg of configs) {
    addList(cfg?.features);
  }
  return [...map.values()];
}

export const DEFAULT_SCHOOL_TYPES = [
  { value: "indian", label: "Indian" },
  { value: "international", label: "International" },
  { value: "custom", label: "Custom" },
];

const DomainFeaturesContext = createContext({
  domainName: "",
  brandDomain: "montestory.in",
  wwwDomain: "www.montestory.in",
  supportEmail: "support@montestory.in",
  siteUrl: "https://www.montestory.in",
  loading: false,
  found: false,
  origin: null,
  features: [],
  configs: [],
  schoolTypeAvailability: {
    indian: true,
    international: true,
    custom: true,
  },
  allowedSchoolTypes: DEFAULT_SCHOOL_TYPES,
  isFeatureEnabled: () => false,
  hasFeatureFlag: () => false,
  useStudentIdLabel: false,
  useEnrollmentDateLabel: false,
  hideAadhaarNumber: false,
  hideMaskedAadhaarCard: false,
  useUsStudentRegistrationUpload: false,
  useNameOnInvoiceLabel: false,
  useZipCodeLabel: false,
  showStateField: false,
  showDpaSignedField: false,
  /** disable_indian_circulum: hide curriculum model field; force International. */
  forceInternationalCurriculum: false,
  hideCurriculumModelField: false,
  refresh: () => {},
});

export { getBrandDomain, getBrandInfo, getSupportEmail, getWwwDomain, getSiteUrl };

/**
 * Current site domain without protocol (e.g. localhost:3000, app.example.com).
 */
export function getCurrentDomainName() {
  if (typeof window === "undefined") return "";
  try {
    const hostname = String(window.location.hostname || "")
      .trim()
      .toLowerCase()
      .replace(/^www\./, "");
    const port = String(window.location.port || "").trim();
    if (port && port !== "80" && port !== "443") {
      return `${hostname}:${port}`;
    }
    return hostname;
  } catch {
    return "";
  }
}

/**
 * Domains that always use International curriculum (no Select Curriculum Model UI).
 * Host only — port stripped for local/preview checks.
 */
export function isInternationalOnlyDomain(domainName) {
  const host = String(domainName || "")
    .trim()
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/:\d+$/, "");
  if (!host) return false;
  return host === "montestory.org" || host.endsWith(".montestory.org");
}

/**
 * Defaults: indian, international, custom all enabled.
 * Flag disable_indian_circulum (enabled: true) → hide curriculum picker; domain is International only.
 */
export function deriveSchoolTypeAvailability(features = [], domainName = "") {
  const availability = {
    indian: true,
    international: true,
    custom: true,
  };

  if (
    isInternationalOnlyDomain(domainName) ||
    hasDisableIndianCurriculumFlag(features)
  ) {
    availability.indian = false;
    availability.custom = false;
    availability.international = true;
  }

  return availability;
}

export function DomainFeaturesProvider({ children }) {
  const [domainName] = useState(() => getCurrentDomainName());
  const [loading, setLoading] = useState(Boolean(domainName));
  const [found, setFound] = useState(false);
  const [origin, setOrigin] = useState(null);
  const [features, setFeatures] = useState([]);
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    const brandDomain = getBrandDomain(domainName);
    if (typeof document !== "undefined" && brandDomain) {
      document.title = `${brandDomain} - Montessori School Management Application`;
    }
  }, [domainName]);

  const load = React.useCallback(() => {
    const domain = getCurrentDomainName();
    if (!domain) {
      setLoading(false);
      setFound(false);
      setFeatures([]);
      setConfigs([]);
      setOrigin(null);
      return Promise.resolve(null);
    }
    setLoading(true);
    return OriginMasterService.getFeaturesByDomain(domain)
      .then((res) => {
        const data = res || {};
        setFound(Boolean(data.found));
        setOrigin(data.origin || null);
        setConfigs(Array.isArray(data.configs) ? data.configs : []);
        // Use top-level `features` or fall back to configs[].features
        setFeatures(resolveFeaturesFromResponse(data));
        return data;
      })
      .catch(() => {
        setFound(false);
        setOrigin(null);
        setFeatures([]);
        setConfigs([]);
        return null;
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const schoolTypeAvailability = useMemo(
    () => deriveSchoolTypeAvailability(features, domainName),
    [features, domainName],
  );

  const allowedSchoolTypes = useMemo(
    () =>
      DEFAULT_SCHOOL_TYPES.filter(
        (t) => schoolTypeAvailability[t.value] !== false,
      ),
    [schoolTypeAvailability],
  );

  const featureMap = useMemo(() => {
    const map = new Map();
    for (const feature of features) {
      const key = featureKey(feature?.name);
      if (!key) continue;
      map.set(key, feature);
    }
    return map;
  }, [features]);

  const isFeatureEnabled = React.useCallback(
    (name) => {
      const feature = featureMap.get(featureKey(name));
      return Boolean(feature && feature.enabled !== false);
    },
    [featureMap],
  );

  const hasFeatureFlag = React.useCallback(
    (name) => featureMap.has(featureKey(name)),
    [featureMap],
  );

  const useStudentIdLabel = isFeatureEnabled(FEATURE_FLAGS.AN_TO_STUDENT_ID);
  const useEnrollmentDateLabel = isFeatureEnabled(
    FEATURE_FLAGS.DOA_TO_ENROLLMENT_DATE,
  );
  const hideAadhaarNumber =
    isInternationalOnlyDomain(domainName) ||
    isFeatureEnabled(FEATURE_FLAGS.DISABLE_AADHAAR_NUMBER);
  const hideMaskedAadhaarCard =
    isInternationalOnlyDomain(domainName) ||
    isFeatureEnabled(FEATURE_FLAGS.DISABLE_MASKED_AADHAAR_CARD);
  const useUsStudentRegistrationUpload = isFeatureEnabled(
    FEATURE_FLAGS.US_VERSION_STUDENT_REGISTRATION_UPLOAD,
  );
  const useNameOnInvoiceLabel = isFeatureEnabled(
    FEATURE_FLAGS.SOCIETY_NAME_TO_NAME_ON_INVOICE,
  );
  const useZipCodeLabel =
    isInternationalOnlyDomain(domainName) ||
    isFeatureEnabled(FEATURE_FLAGS.PIN_CODE_TO_ZIP_CODE) ||
    isEnabledFeatureMatch(
      features,
      FEATURE_FLAGS.PIN_CODE_TO_ZIP_CODE,
      "Pin_Code_to_Zip_Code",
      "Pincode_to_Zip_Code",
      "PinCode_to_ZipCode",
      "Pin_Code_to_ZipCode",
      "Pin_Code_change_as_Zip_Code",
      "change_Pin_Code_to_Zip_Code",
    );
  const showStateField =
    isFeatureEnabled(FEATURE_FLAGS.STATE) ||
    isEnabledFeatureMatch(
      features,
      FEATURE_FLAGS.STATE,
      "State",
      "US_State",
      "show_State",
      "State_Field",
      "add_State",
    );
  const showDpaSignedField =
    isFeatureEnabled(FEATURE_FLAGS.DPA_SIGNED) ||
    isEnabledFeatureMatch(
      features,
      FEATURE_FLAGS.DPA_SIGNED,
      "DPA_Signed",
      "DPA Signed",
      "DPA_Signed_YN",
      "show_DPA_Signed",
      "DPA",
    );
  const forceInternationalCurriculum =
    isInternationalOnlyDomain(domainName) ||
    hasDisableIndianCurriculumFlag(features);
  const hideCurriculumModelField =
    forceInternationalCurriculum ||
    (allowedSchoolTypes.length === 1 &&
      allowedSchoolTypes[0]?.value === "international");

  const brand = useMemo(() => getBrandInfo(domainName), [domainName]);

  const value = useMemo(
    () => ({
      domainName,
      brandDomain: brand.brandDomain,
      wwwDomain: brand.wwwDomain,
      supportEmail: brand.supportEmail,
      siteUrl: brand.siteUrl,
      loading,
      found,
      origin,
      features,
      configs,
      schoolTypeAvailability,
      allowedSchoolTypes,
      isFeatureEnabled,
      hasFeatureFlag,
      useStudentIdLabel,
      useEnrollmentDateLabel,
      hideAadhaarNumber,
      hideMaskedAadhaarCard,
      useUsStudentRegistrationUpload,
      useNameOnInvoiceLabel,
      useZipCodeLabel,
      showStateField,
      showDpaSignedField,
      forceInternationalCurriculum,
      hideCurriculumModelField,
      refresh: load,
    }),
    [
      domainName,
      brand,
      loading,
      found,
      origin,
      features,
      configs,
      schoolTypeAvailability,
      allowedSchoolTypes,
      isFeatureEnabled,
      hasFeatureFlag,
      useStudentIdLabel,
      useEnrollmentDateLabel,
      hideAadhaarNumber,
      hideMaskedAadhaarCard,
      useUsStudentRegistrationUpload,
      useNameOnInvoiceLabel,
      useZipCodeLabel,
      showStateField,
      showDpaSignedField,
      forceInternationalCurriculum,
      hideCurriculumModelField,
      load,
    ],
  );

  return (
    <DomainFeaturesContext.Provider value={value}>
      {children}
    </DomainFeaturesContext.Provider>
  );
}

export function useDomainFeatures() {
  return useContext(DomainFeaturesContext);
}

export default DomainFeaturesContext;
